import { useEffect, useState, useCallback } from 'react';
import { useValidateSchema } from 'src/hooks';
import { SimilarByPatternSettingsReducerState } from 'src/app-state-types';
import {
  UseSimilarByPatternSettingsDraftArgs,
  UseSimilarByPatternSettingsDraft,
  SimilarByPatternSettingsRecord,
} from './types';
import { similarByPatternSettingsValidationSchema } from './constants';
import { similarByPatternSettingsActionsMethods } from './Actions';
import { isDataDirty } from '../../utils';

export function useSimilarByPatternSettingsDraft({
  similarByPatternSettings,
  dispatch,
  shopId,
  onIsDirty,
  shouldRefetch = false,
}: UseSimilarByPatternSettingsDraftArgs): UseSimilarByPatternSettingsDraft {
  const [draftSimilarByPatternSettings, setDraftSimilarByPatternSettings] =
    useState(similarByPatternSettings);

  const { errors, validate, isValid } = useValidateSchema<SimilarByPatternSettingsRecord>({
    schema: similarByPatternSettingsValidationSchema,
  });

  const isDirty = isDataDirty(similarByPatternSettings, draftSimilarByPatternSettings);

  const onSubmit = (): void => {
    if (isValid) {
      dispatch(
        similarByPatternSettingsActionsMethods.updateSimilarByPatternSettings({
          shopId,
          numberOfResultsToDisplay: draftSimilarByPatternSettings.numberOfResultsToDisplay,
        })
      );
    }
  };

  const onDiscard = (): void => {
    setDraftSimilarByPatternSettings(similarByPatternSettings);
  };

  const onChange = useCallback(
    (partialSettings: Partial<SimilarByPatternSettingsReducerState>) => {
      const updatedState = { ...draftSimilarByPatternSettings, ...partialSettings };
      validate({ dataToValidate: updatedState });
      setDraftSimilarByPatternSettings(updatedState);
    },
    [draftSimilarByPatternSettings, validate, setDraftSimilarByPatternSettings]
  );

  const fetchSimilarByPatternSettings = useCallback(() => {
    dispatch(
      similarByPatternSettingsActionsMethods.fetchSimilarByPatternSettings({
        shopId,
      })
    );
  }, [shopId]);

  useEffect((): void => {
    setDraftSimilarByPatternSettings(similarByPatternSettings);
  }, [similarByPatternSettings]);

  useEffect(() => {
    validate({ dataToValidate: draftSimilarByPatternSettings });
  }, [draftSimilarByPatternSettings]);

  useEffect(() => {
    const payload = { isDirty: !!isDirty };
    if (onIsDirty) {
      onIsDirty(payload);
    } else {
      dispatch(similarByPatternSettingsActionsMethods.notifyIsDirty(payload));
    }
  }, [isDirty]);

  useEffect(() => {
    fetchSimilarByPatternSettings();
  }, [fetchSimilarByPatternSettings]);

  useEffect(() => {
    if (shouldRefetch) {
      fetchSimilarByPatternSettings();
    }
  }, [shouldRefetch]);

  return {
    draftSimilarByPatternSettings,
    errors,
    isValid,
    isDirty,
    onSubmit,
    onDiscard,
    onChange,
  };
}
